import { useEffect, useRef, useState } from "react";
import { Control, FieldPathByValue, FieldValues, UseControllerProps, useController } from "react-hook-form";
import Autocomplete from "@/components/Autocomplete";
import { placeToFormData } from "@/components/AddressAutoComplete/utils";
import getPlaces from "@/components/AddressAutoComplete/loadGooglePlaces";

type Item = { value: string; label: string };

type Places = {
  getPlacePredictions: google.maps.places.AutocompleteService["getPlacePredictions"];
  getDetails: google.maps.places.PlacesService["getDetails"];
};

interface AddressAutocompleteProps<T extends FieldValues> {
  name: FieldPathByValue<T, string>;
  control: Control<T>;
  rules?: UseControllerProps<T, FieldPathByValue<T, string>>["rules"];
  label: string;
  onReset?(): void;
  required?: boolean;
}

function renderItem({ label }: Item) {
  return <span>{label}</span>;
}

function LocationAutocompleteInput<T extends FieldValues>({
  name,
  label,
  control,
  rules,
  onReset,
  ...inputProps
}: AddressAutocompleteProps<T>) {
  const [data, setData] = useState<Item[]>([]);

  const attributionRef = useRef<HTMLDivElement>(null);

  const placesLibRef = useRef<Places>();

  const {
    field: { ref: formRef, ...field },
    fieldState: { error },
    formState: { isSubmitting }
  } = useController({ control, name, rules: { ...rules } });

  useEffect(() => {
    getPlaces(attributionRef.current!).then((places) => {
      placesLibRef.current = places;
    });
  }, []);

  const getPlacePreditions = (input: string) => {
    const places = placesLibRef.current;
    if (places)
      return places.getPlacePredictions({
        input,
        types: ["locality", "postal_code"],
        componentRestrictions: { country: "USA" }
      });
    else throw "cannot load places lib";
  };

  async function onChangeText(input: string) {
    field.onChange(input);

    if (input) {
      const response = await getPlacePreditions(input);
      if (response?.predictions.length)
        setData(response.predictions.map(({ description, place_id }) => ({ value: place_id, label: description })));
    }
  }

  function onSelect(value: Item) {
    field.onChange(value.label);
    placesLibRef.current?.getDetails({ placeId: value.value, fields: ["address_components"] }, (place) => {
      if (place) {
        const { city, state } = placeToFormData(place);
        field.onChange(`${city ? `${city},` : ""} ${state}` || "");
      }
    });
  }

  return (
    <>
      <div ref={attributionRef} />
      <Autocomplete
        data={data}
        error={!!error}
        errorText={error?.message}
        label={label}
        onChangeText={onChangeText}
        onSelect={onSelect}
        renderItem={renderItem}
        reset={onReset}
        leadingIcon={null}
        query={field.value}
        disabled={isSubmitting}
        {...inputProps}
      />
    </>
  );
}

export default LocationAutocompleteInput;
